import moment from 'moment';

import { DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT } from '@/config';

export const getTimeDifference = (
  startTime: moment.Moment,
  originTime: Date,
) => {
  const difference = startTime.diff(moment(originTime));
  // there is an error Uncaught SyntaxError in the browser if i follow the ESLINT linter
  //: The requested module '/node_modules/.vite/deps/moment.js?v=0be6047f' does not provide an export named 'duration'
  // eslint-disable-next-line import/no-named-as-default-member
  return moment.utc(moment.duration(difference).asMilliseconds());
};

export const getFormattedDateTime = (date?: string) => {
  if (!date) return '';
  return moment(date).format(DATE_TIME_FORMAT);
};

export const getFormattedTime = (date?: string) => {
  if (!date) return '';
  return moment(date).format(TIME_FORMAT);
};

export const getFormattedDate = (
  date?: string | Date | null,
  format = DATE_FORMAT,
) => {
  if (!date) return '';
  return moment(date).format(format);
};

/**
 * Format a date as "12th day of March, 2026" — the long-form expected by the
 * Monticello Arrest Warrant. Returns '' for nullish/invalid input.
 */
export const formatOrdinalDate = (date?: Date | string | null): string => {
  if (!date) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  return `${m.format('Do')} day of ${m.format('MMMM, YYYY')}`;
};

/**
 * Parse a date-only string (YYYY-MM-DD) as a LOCAL date, avoiding the UTC
 * midnight shift that `new Date("YYYY-MM-DD")` causes in timezones west of UTC.
 *
 * Background: the ECMAScript spec treats ISO date-only strings as UTC midnight.
 * In US Eastern (UTC-5/-4) that shifts the displayed date one day backwards.
 * moment() always parses to local time, so this is safe across all timezones.
 *
 * Use this everywhere a date-only string must be rendered or compared as a date.
 */
export const parseLocalDate = (
  dateStr: string | null | undefined,
): Date | null => {
  if (!dateStr) return null;
  const m = moment(String(dateStr), 'YYYY-MM-DD', true);
  if (m.isValid()) return m.toDate();
  // fallback for non-ISO strings — moment parses to local time
  const fallback = moment(String(dateStr));
  return fallback.isValid() ? fallback.toDate() : null;
};

export const toDateOnlyString = (value?: string | Date | null): string => {
  if (!value) return '';

  if (value instanceof Date) {
    // Date-only values are stored as UTC midnight, so read the UTC calendar
    // day; String(date) would render local wall-clock text and re-shift it.
    return Number.isNaN(value.getTime())
      ? ''
      : value.toISOString().slice(0, 10);
  }

  const rawValue = String(value).trim();
  if (!rawValue) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    return rawValue;
  }

  const dateOnlyFromIso = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnlyFromIso?.[1]) {
    return dateOnlyFromIso[1];
  }

  const parsedDate = moment(rawValue);
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
};

/**
 * Render a stored date-only value (e.g. a warrant's Date Issued) as a display
 * string without the UTC-midnight shift: slices the calendar date with
 * toDateOnlyString (no timezone math), then formats via getFormattedDate.
 */
export const formatDateOnly = (value?: string | Date | null): string =>
  getFormattedDate(toDateOnlyString(value));

/**
 * Calculates the duration between a timestamp and now, then formats it.
 * - If less than 1 hour, formats as minutes with one decimal place (e.g., "5.2m", "49.5m").
 * - If 1 hour or more, formats as hours and minutes (e.g., "1h 15m", "2h 0m").
 * @param timestamp The starting timestamp string.
 * @returns Formatted duration string or 'N/A' if timestamp is invalid.
 */
export const formatDurationFromTimestamp = (
  timestamp: string | null,
): string => {
  if (!timestamp) {
    return 'N/A';
  }

  const startTime = moment(timestamp);
  const now = moment();
  // TODO: check the warning
  // eslint-disable-next-line import/no-named-as-default-member
  const duration = moment.duration(now.diff(startTime));
  const totalMinutes = duration.asMinutes();

  if (totalMinutes < 0) {
    return '0.0m'; // Handle potential clock skew
  }

  if (totalMinutes < 60) {
    // Format as minutes with one decimal place
    return `${totalMinutes.toFixed(1)}m`;
  } else {
    // Format as hours and minutes
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  }
};

export const getCompactTimeAgo = (
  value?: string | Date | null,
  nowValue?: string | Date,
): string => {
  const parseInputToDate = (input: string | Date): Date | null => {
    if (input instanceof Date) {
      return Number.isNaN(input.getTime()) ? null : input;
    }

    return Number.isNaN(Date.parse(input)) ? null : new Date(input);
  };

  if (!value) {
    return '-';
  }

  const parsedTimestamp = parseInputToDate(value);
  if (!parsedTimestamp) {
    return '-';
  }

  const timestamp = moment(parsedTimestamp);
  if (!timestamp.isValid()) {
    return '-';
  }

  const parsedNow = nowValue ? parseInputToDate(nowValue) : new Date();
  if (!parsedNow) {
    return '-';
  }

  const now = moment(parsedNow);
  if (!now.isValid()) {
    return '-';
  }

  const diffInMinutes = Math.max(0, Math.floor(now.diff(timestamp, 'minutes')));

  if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d`;
};

export const getUpdatedLabel = (
  value?: string | Date | null,
  nowValue?: string | Date,
): string => {
  const compactTime = getCompactTimeAgo(value, nowValue);

  if (compactTime === '-') {
    return '-';
  }

  return `Updated ${compactTime} ago`;
};

/**
 * Years from `birthday` to now, floored. Returns null when the input is
 * missing, invalid, or resolves to a negative age (future DOB).
 */
export const calculateAgeYears = (
  birthday?: string | Date | null,
): number | null => {
  if (!birthday) return null;
  const birth = moment(birthday);
  if (!birth.isValid()) return null;
  const age = moment().diff(birth, 'years');
  return age >= 0 ? age : null;
};
